// Guide content for /resources/[slug] pages.
// Each guide includes: title, description, TOC sections, and 5-8 FAQ Q&As.

export type Guide = {
  slug: string;
  title: string;
  metaTitle: string;
  description: string;
  ctaPlan: "full" | "refusal" | "basic";
  toc: { id: string; heading: string; body: string[] }[];
  faqs: { q: string; a: string }[];
};

export const GUIDES: Guide[] = [
  {
    slug: "why-f1-visas-refused-214b-2026",
    title: "Why Do F1 Visas Get Refused Under 214(b)? A 2026 Breakdown",
    metaTitle: "Why F1 Visas Get Refused Under 214(b) in 2026 | WeLivdIt",
    description:
      "The five patterns behind almost every F-1 214(b) refusal in 2026 - weak finances, weak home ties, communication gaps, DS-160 mismatches, and profile red flags.",
    ctaPlan: "refusal",
    toc: [
      {
        id: "what-is-214b",
        heading: "What section 214(b) actually says",
        body: [
          "214(b) is a clause in the US Immigration & Nationality Act. It presumes every nonimmigrant applicant is an intending immigrant until they prove otherwise. The applicant carries the burden of proof - not the officer.",
          "That is why 'they didn't ask enough questions' is not really a reason - officers refuse when the burden is not met, whether they asked 3 questions or 15.",
        ],
      },
      {
        id: "pattern-1",
        heading: "Pattern 1: Weak or unclear finances",
        body: [
          "The number-one cause of F-1 214(b) at Indian consulates in 2026 remains sponsor/financials. Sudden lumps in bank statements, ITR that doesn't cover tuition + living, and 'my uncle will help' answers all fail.",
          "Fix: one sponsor, 2-3 years of clean statements, one crisp sentence on total 2-year cost, and a loan letter memorized cold if you're loan-funded.",
        ],
      },
      {
        id: "pattern-2",
        heading: "Pattern 2: No visible ties to home country",
        body: [
          "Officers listen for a reason you'll come back. 'My family is here' alone isn't enough - a family business, property, job offer, or specific home-country career plan is.",
        ],
      },
      {
        id: "pattern-3",
        heading: "Pattern 3: Communication and confidence",
        body: [
          "Rehearsed scripts get spotted in the first 30 seconds. Long, hesitant, or self-contradicting answers cause the officer to stop asking.",
          "Keep answers 15-25 seconds. Speak the structure, not the script.",
        ],
      },
      {
        id: "pattern-4",
        heading: "Pattern 4: DS-160 mismatches",
        body: [
          "Anything in your DS-160 that doesn't match your verbal answer is a red flag - past visas, address history, list of universities applied, or an unrelated field of study.",
        ],
      },
      {
        id: "pattern-5",
        heading: "Pattern 5: Profile red flags",
        body: [
          "Academic gaps, prior refusals with no changes, or siblings already in the US can put your case in the harder bucket. They don't refuse you on their own but they raise the bar for everything else.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is 214(b) permanent?",
        a: "No. 214(b) is not a permanent bar. You can reapply as soon as you've fixed what caused it, though re-attempting with the same story usually fails again.",
      },
      {
        q: "How many times can I reapply after 214(b)?",
        a: "There's no cap in the law, but most consulates track re-attempts. After 2-3 refusals with no change, approvals get much harder.",
      },
      {
        q: "Do I need a new DS-160 for each attempt?",
        a: "Yes, and you should update it - if it matches your previous refused DS-160 exactly, expect the same outcome.",
      },
      {
        q: "Does a higher-ranked university help?",
        a: "Only if you can explain why you chose it. Officers refuse well-ranked applicants and approve state-university applicants every day.",
      },
      {
        q: "Can WeLivdIt guarantee approval?",
        a: "No coaching provider can. What we can do is diagnose why you were refused and rebuild every piece before you re-book.",
      },
      {
        q: "What is the difference between 214(b) and 221(g)?",
        a: "214(b) is a refusal for lack of nonimmigrant intent. 221(g) is a pause for missing documents or administrative processing - it's not a refusal in the same sense.",
      },
    ],
  },
  {
    slug: "home-ties-answer-visa-officer",
    title: "What to Say When the Officer Asks About Ties to Home Country",
    metaTitle: "Ties to Home Country: US Visa Interview Answers | WeLivdIt",
    description:
      "Officers ask about home ties to test your nonimmigrant intent. Here's how to structure a 20-second answer that survives follow-up in 2026.",
    ctaPlan: "full",
    toc: [
      {
        id: "why-they-ask",
        heading: "Why officers ask this question",
        body: [
          "Under section 214(b), you must show you intend to return home. Home ties are the shorthand for that - family, property, career, and business that make returning the rational choice.",
        ],
      },
      {
        id: "structure",
        heading: "The 3-part structure that works",
        body: [
          "1) A concrete reason (family business, property, offer letter).",
          "2) A specific post-graduation plan (sector, city, role - not 'get a job').",
          "3) One sentence that ties it to your degree.",
        ],
      },
      {
        id: "avoid",
        heading: "What NOT to say",
        body: [
          "'My parents live in India' alone is weak. So is 'I love my country.' Officers want economic and career gravity, not sentiment.",
          "Never say 'I'll decide after I graduate' or leave the H-1B door open explicitly.",
        ],
      },
      {
        id: "examples",
        heading: "Two example answers",
        body: [
          "For a family-business applicant: 'My family runs a manufacturing unit in Pune. I'm the only child and will take over the finance side after my MS in Analytics. That role is waiting for me.'",
          "For an offer-letter applicant: 'I have a return offer with TCS's US-shift analytics team based in Hyderabad. My MS is directly aligned with that role.'",
        ],
      },
    ],
    faqs: [
      {
        q: "Do I need documents to prove home ties?",
        a: "Not always required - but property papers, business documents, or offer letters help if the officer asks. You rarely get to show documents at Indian consulates.",
      },
      {
        q: "What if I don't have a family business or job offer?",
        a: "Build a specific plan - target sector, target companies, target role, and salary range. Vague plans read as 'I'll stay in the US.'",
      },
      {
        q: "Should I mention I might do H-1B?",
        a: "No. Nonimmigrant intent means you plan to return. Mentioning H-1B or 'settle' is a near-instant 214(b).",
      },
      {
        q: "What if I have siblings in the US?",
        a: "Acknowledge it briefly and pivot to why YOU are returning. Officers will ask, so don't hide it.",
      },
      {
        q: "Can I say my parents need me back?",
        a: "It helps but shouldn't be the only reason. Combine it with a career/business reason for weight.",
      },
    ],
  },
  {
    slug: "214b-vs-221g-difference",
    title: "214(b) vs 221(g): What's the Difference?",
    metaTitle: "214(b) vs 221(g) US Visa Refusal Explained | WeLivdIt",
    description:
      "214(b) is a full refusal for lack of nonimmigrant intent. 221(g) is a pause for documents or administrative processing. Here's how to tell them apart and what to do next.",
    ctaPlan: "refusal",
    toc: [
      {
        id: "214b",
        heading: "What is 214(b)?",
        body: [
          "A 214(b) is a full refusal under the presumption of immigrant intent. The officer decided your case did not overcome that presumption. You leave with a printed 214(b) slip and no visa.",
        ],
      },
      {
        id: "221g",
        heading: "What is 221(g)?",
        body: [
          "221(g) is a temporary refusal - either you need to submit more documents, or your case is under administrative processing. You'll get a coloured slip (yellow, pink, blue depending on consulate) and a follow-up window.",
        ],
      },
      {
        id: "diff",
        heading: "Key differences at a glance",
        body: [
          "214(b): full refusal, no visa, must re-apply from scratch with changes.",
          "221(g): case not decided yet, either submit documents or wait for admin processing to clear.",
        ],
      },
      {
        id: "next",
        heading: "What to do next",
        body: [
          "For 214(b): diagnose the root cause, fix DS-160 + story + documents, then re-book only when the case is materially different.",
          "For 221(g): submit exactly what's requested, within the window given. Don't argue - just comply.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is 221(g) a refusal?",
        a: "Technically it counts as a refusal on paper but it's not a final decision - the case can still be approved after documents or clearance.",
      },
      {
        q: "How long does 221(g) administrative processing take?",
        a: "Anywhere from 2 weeks to 6 months in 2026, depending on the reason. Some cases (SEVIS, security clearance) go longer.",
      },
      {
        q: "Can 221(g) become a 214(b)?",
        a: "Yes - if the requested documents don't resolve the concern, the case may convert to 214(b). It's rare but happens.",
      },
      {
        q: "Do I need to reapply after 221(g)?",
        a: "No, not immediately - complete the 221(g) requirement first. Only reapply if the case is finally refused.",
      },
      {
        q: "Which slip did I get?",
        a: "The colour and text of the slip tells you - 214(b) says 'refused under 214(b)'; 221(g) lists 'administrative processing' or 'additional documents required'.",
      },
    ],
  },
  {
    slug: "reapply-f1-after-refusal-how-soon",
    title: "Can You Reapply for F1 Visa After Refusal? How Soon?",
    metaTitle: "Reapply for F1 Visa After Refusal: How Soon? | WeLivdIt",
    description:
      "You can reapply immediately - but with 2026 consulate wait times exceeding 300+ days, re-booking blindly is dangerous. Here's how to decide when to re-book after a 214(b) F-1 refusal.",
    ctaPlan: "refusal",
    toc: [
      {
        id: "when",
        heading: "When you CAN reapply",
        body: [
          "Legally, immediately. However, with standard appointment wait times stretching past 300+ days (10+ months) in 2026, securing a refusal slot requires precise timing and immediate preparation.",
        ],
      },
      {
        id: "when-not",
        heading: "When you SHOULD wait",
        body: [
          "Wait until three things are true: (1) you know exactly why you were refused, (2) you've changed at least one material thing (documents, DS-160, story, or profile), and (3) you can explain the change in 20 seconds.",
        ],
      },
      {
        id: "timeline",
        heading: "Realistic timeline in 2026",
        body: [
          "Same intake (Fall / Spring): Given the 300+ day general appointment backlog",
          "Deferred to next intake: If the fix is big (loan restructuring, new offer letter, university change), deferring gives you 3-4 months to clear the wait-time hurdle with a stronger application.",
        ],
      },
    ],
    faqs: [
      {
        q: "How do 300+ day wait times affect refusal re-attempts?",
        a: "With standard wait times exceeding 300+ days, most re-applicants rely on emergency slot requests or expedited refusal appointments. Consulates strictly deny emergency requests for previous refused folks.",
      },
      {
        q: "How many refusals before it's over?",
        a: "There's no legal cap but after 2-3 refusals with no material change, approval odds drop sharply. Fix the case, don't re-book blindly.",
      },
      {
        q: "Does the officer see previous refusals?",
        a: "Yes - every previous refusal at any US consulate is visible. Address it directly.",
      },
      {
        q: "Can I switch consulates for a re-attempt?",
        a: "You can, but it doesn't reset your history. The officer sees the prior refusal regardless of consulate.",
      },
      {
        q: "Should I switch universities before reapplying?",
        a: "Only if the university itself was the weak link (rank too low, no scholarship, no fit). Otherwise it looks like you're chasing a visa, not a program.",
      },
      {
        q: "Do I need a fresh SEVIS I-20?",
        a: "Yes if your intake shifted or your I-20 expired. Same intake with a valid I-20 - not required.",
      },
    ],
  },
  {
    slug: "first-30-seconds-of-f1-interview",
    title: "F1 Visa Interview: What Happens in the First 30 Seconds",
    metaTitle: "F1 Visa Interview: The First 30 Seconds | WeLivdIt",
    description:
      "Officers make an initial judgment inside the first 30 seconds of your F-1 interview. Here's exactly what they're watching for and how to nail the opening.",
    ctaPlan: "full",
    toc: [
      {
        id: "approach",
        heading: "Step 1: Walking up to the window",
        body: [
          "Officers watch how you approach - posture, eye contact, and whether you look prepared or nervous. Look up, walk steadily, smile briefly.",
        ],
      },
      {
        id: "greeting",
        heading: "Step 2: The greeting",
        body: [
          "'Good morning' - hand over the passport / DS-160 barcode. Do not launch into a speech. Wait for the first question.",
        ],
      },
      {
        id: "first-q",
        heading: "Step 3: The first real question",
        body: [
          "Almost always: 'Why this university?' or 'What are you planning to study?' Your first answer sets the tone. Keep it 15-20 seconds, specific, and confident.",
        ],
      },
      {
        id: "signals",
        heading: "What officers actually score",
        body: ["Speed of response, eye contact, specificity, and internal consistency with what's on the DS-160."],
      },
    ],
    faqs: [
      {
        q: "Does the officer see my documents?",
        a: "They see the DS-160 on their screen. They rarely ask for physical documents at Indian consulates.",
      },
      { q: "How long is a typical F-1 interview?", a: "2-4 minutes on average. Some are decided in 60 seconds." },
      {
        q: "Can I ask the officer a clarifying question?",
        a: "You can, briefly - but don't stall. If you don't understand, say 'Could you repeat, please?' once.",
      },
      {
        q: "Does my dress affect the outcome?",
        a: "Not directly - but neat, business-casual signals seriousness. Overdressed suits or casual t-shirts both hurt.",
      },
      {
        q: "What if I go blank on the first question?",
        a: "Pause 2 seconds, take a breath, and answer. Officers accept a small pause; they don't accept a rambling save.",
      },
    ],
  },
  {
    slug: "b1-b2-visa-refusal-reasons",
    title: "B1/B2 Visa Refusal: Common Reasons and How to Fix Them",
    metaTitle: "B1/B2 Visa Refusal: Reasons and How to Fix | WeLivdIt",
    description:
      "B1/B2 refusals almost always come down to weak ties, unclear purpose, or unexplained sponsors. Here's what to fix before your re-attempt.",
    ctaPlan: "refusal",
    toc: [
      {
        id: "reasons",
        heading: "The top B1/B2 refusal reasons in 2026",
        body: [
          "Weak home ties (retirees or young applicants with no property/job).",
          "Unclear purpose ('visiting my son' with no return date).",
          "Sponsor mismatch (relative in the US doesn't match the visitor pattern).",
        ],
      },
      {
        id: "fix-ties",
        heading: "Fixing home ties",
        body: [
          "Property, family business, ongoing job, or a very clear return-by date all help. A retiree with a return ticket + ongoing family responsibility in India is a strong case.",
        ],
      },
      {
        id: "fix-purpose",
        heading: "Fixing purpose",
        body: [
          "Specific: dates, cities, hosts, and return ticket. 'Attending my grandson's graduation, staying 20 days, returning Aug 10' beats 'visiting family.'",
        ],
      },
    ],
    faqs: [
      {
        q: "Are B1/B2 refusals harder to overturn than F-1?",
        a: "Often yes - because there's no 'ongoing course of study' to anchor the case. Home ties do all the work.",
      },
      {
        q: "Can parents visit if the applicant has family in the US?",
        a: "Yes, most approvals are exactly this pattern. Just show they return home.",
      },
      {
        q: "Does buying a return ticket help?",
        a: "Marginally. It signals intent but officers know tickets can be changed.",
      },
      { q: "How soon can B1/B2 reapply after refusal?", a: "Same as F-1 - immediately in theory. Fix the case first." },
      {
        q: "Do I need travel insurance to reapply?",
        a: "Not required for the visa - but showing organized planning helps the officer trust the case.",
      },
    ],
  },
];

export const GUIDES_BY_SLUG: Record<string, Guide> = Object.fromEntries(GUIDES.map((g) => [g.slug, g]));
